'use client';

// src/css-utils.ts
var adoptedStylesSupported = typeof window !== "undefined" && window.ShadowRoot?.prototype.hasOwnProperty("adoptedStyleSheets") && window.CSSStyleSheet?.prototype.hasOwnProperty("replace");
var isCSSStyleSheet = (stylesheet) => {
  return typeof CSSStyleSheet !== "undefined" && stylesheet instanceof CSSStyleSheet;
};
var getTaggedTemplateStr = (strArr, ...interpolated) => {
  return strArr.reduce((resultStr, currentStr, i) => {
    return resultStr + currentStr + (interpolated[i] ?? "");
  }, "");
};
var css = (strArr, ...interpolated) => {
  const styles = getTaggedTemplateStr(strArr, ...interpolated);
  if (adoptedStylesSupported) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styles);
    return sheet;
  }
  return styles;
};
var stylesheetMap = /* @__PURE__ */ new Map();
var useCSS = (key) => {
  return (strArr, ...interpolated) => {
    const symbol = key ?? Symbol();
    const existingStylesheet = stylesheetMap.get(symbol);
    if (existingStylesheet) {
      const styles = getTaggedTemplateStr(strArr, ...interpolated);
      if (isCSSStyleSheet(existingStylesheet)) {
        existingStylesheet.replace(styles);
        return existingStylesheet;
      }
      stylesheetMap.set(symbol, styles);
      return styles;
    }
    const stylesheet = css(strArr, ...interpolated);
    stylesheetMap.set(symbol, stylesheet);
    return stylesheet;
  };
};
var normalizedScope = `
@layer normalize {
  main { display: block; }
  h1 { font-size: 2em; margin: 0.67em 0; }
  hr { box-sizing: content-box; height: 0; overflow: visible; }
  pre { font-family: monospace, monospace; font-size: 1em; }
  a { background-color: transparent; }
  abbr[title] { border-bottom: none; text-decoration: underline; text-decoration: underline dotted; }
  b, strong { font-weight: bolder; }
  code, kbd, samp { font-family: monospace, monospace; font-size: 1em; }
  small { font-size: 80%; }
  sub, sup { font-size: 75%; line-height: 0; position: relative; vertical-align: baseline; }
  sub { bottom: -0.25em; }
  sup { top: -0.5em; }
  img { border-style: none; }
  button, input, optgroup, select, textarea { font-family: inherit; font-size: 100%; line-height: 1.15; margin: 0; }
  button, input { overflow: visible; }
  button, select { text-transform: none; }
  button, [type="button"], [type="reset"], [type="submit"] { -webkit-appearance: button; }
  button::-moz-focus-inner, [type="button"]::-moz-focus-inner, [type="reset"]::-moz-focus-inner, [type="submit"]::-moz-focus-inner { border-style: none; padding: 0; }
  button:-moz-focusring, [type="button"]:-moz-focusring, [type="reset"]:-moz-focusring, [type="submit"]:-moz-focusring { outline: 1px dotted ButtonText; }
  fieldset { padding: 0.35em 0.75em 0.625em; }
  legend { box-sizing: border-box; color: inherit; display: table; max-width: 100%; padding: 0; white-space: normal; }
  progress { vertical-align: baseline; }
  textarea { overflow: auto; }
  [type="checkbox"], [type="radio"] { box-sizing: border-box; padding: 0; }
  [type="number"]::-webkit-inner-spin-button, [type="number"]::-webkit-outer-spin-button { height: auto; }
  [type="search"] { -webkit-appearance: textfield; outline-offset: -2px; }
  [type="search"]::-webkit-search-decoration { -webkit-appearance: none; }
  ::-webkit-file-upload-button { -webkit-appearance: button; font: inherit; }
  details { display: block; }
  summary { display: list-item; }
  [hidden] { display: none; }
}
`;
var normalizedStyles = `
@layer normalize {
  html { line-height: 1.15; -webkit-text-size-adjust: 100%; }
  body { margin: 0; }
  template { display: none; }
}
${normalizedScope}
`;

// src/scope.tsx
import React6 from "react";

// src/template.tsx
import React3 from "react";
import ReactDOM from "react-dom";

// src/context.tsx
import React from "react";
var DEFAULT_SHADOW_SCOPE_CONTEXT = {
  dsd: "on"
};
var ShadowScopeContext = React.createContext(DEFAULT_SHADOW_SCOPE_CONTEXT);
var ShadowScopeConfigProvider = ({ config, children }) => {
  return /* @__PURE__ */ React.createElement(ShadowScopeContext.Provider, { value: config }, children);
};

// src/children-utils.tsx
import React2 from "react";
var replaceSlots = (children, slots) => {
  return React2.Children.map(children, (child) => {
    if (React2.isValidElement(child)) {
      if (child.type === "slot") {
        const slotName = child.props.name ?? "default";
        return slotName in slots ? /* @__PURE__ */ React2.createElement(React2.Fragment, null, slots[slotName]) : child;
      } else if ("children" in child.props) {
        return React2.cloneElement(child, {
          children: replaceSlots(child.props.children, slots)
        });
      }
    }
    return child;
  });
};
var parseSlots = (children, template) => {
  const slots = {};
  React2.Children.forEach(children, (child) => {
    if (React2.isValidElement(child)) {
      const slotName = child.props.slot ?? "default";
      if (!Array.isArray(slots[slotName])) {
        slots[slotName] = [];
      }
      slots[slotName].push(child);
    }
  });
  return replaceSlots(template.props.children, slots);
};

// src/template.tsx
var declarativeShadowDOMSupported = null;
function checkDSDSupport() {
  if (typeof window === "undefined")
    return true;
  if (declarativeShadowDOMSupported !== null)
    return declarativeShadowDOMSupported;
  if (typeof DOMParser === "undefined")
    return false;
  const fragment = new DOMParser().parseFromString(
    '<div><template shadowrootmode="open"></template></div>',
    "text/html",
    // @ts-ignore-next-line; TS doesn't know `parseFromString` supports a 3rd argument.
    { includeShadowRoots: true }
  );
  declarativeShadowDOMSupported = !!fragment.querySelector("div")?.shadowRoot;
  return declarativeShadowDOMSupported;
}
var ATTACH_SHADOW_ERR = `Could not attach the shadow root. The element may already have a shadow root attached. This could be due to HMR in development, and may not indicate a problem. Try refreshing the page to see if this warning goes away.

If the issue persists, other code may be conflicting by attaching a shadow root before this \`<Template>\` instance does.`;
var RENDER_SHADOW_ERR = `Could not render the contents in the shadow root. This could be due to HMR and a failure to reattach a closed shadow root. Refresh the page to initialize a new shadow root.

If you are experiencing this issue at runtime and not during development, you may be directly re-mounting the \`<Template>\` component. This should be avoided in favor of re-mounting its parent.`;
var getMismatchErr = (type) => `Could not update \`${type}\` in the shadow root options because the shadow root can only be initialized once. Refresh the page or replace the parent node with an entirely new instance.`;
var Template = React3.forwardRef((props, forwardedRef) => {
  const {
    children,
    delegatesFocus = false,
    shadowrootmode,
    adoptedStyleSheets = [],
    ...forwardedProps
  } = props;
  const shadowScopeContext = React3.useContext(ShadowScopeContext);
  const { cssStyleSheets, cssStrings } = React3.useMemo(
    () => {
      const cssStyleSheets2 = [];
      const cssStrings2 = [];
      for (const stylesheet of adoptedStyleSheets) {
        if (isCSSStyleSheet(stylesheet)) {
          cssStyleSheets2.push(stylesheet);
        } else if (typeof stylesheet === "string") {
          cssStrings2.push(stylesheet);
        } else {
          console.warn(
            "An invalid stylesheet was passed to `<Template>`, skipping..."
          );
        }
      }
      return { cssStyleSheets: cssStyleSheets2, cssStrings: cssStrings2 };
    },
    [adoptedStyleSheets]
  );
  const shadowRootRef = React3.useRef(null);
  const templateRef = React3.useRef(null);
  const shadowOptsRef = React3.useRef({
    delegatesFocus,
    mode: shadowrootmode ?? "open"
  });
  const [initialized, setInitialized] = React3.useState(false);
  const dsdSupported = React3.useMemo(
    () => checkDSDSupport() && shadowScopeContext.dsd === "on",
    [declarativeShadowDOMSupported, shadowScopeContext.dsd]
  );
  React3.useEffect(() => {
    if (shadowRootRef.current !== null) {
      shadowRootRef.current.adoptedStyleSheets = cssStyleSheets;
    }
  }, [shadowRootRef, initialized, cssStyleSheets]);
  React3.useEffect(() => {
    if (templateRef.current === null)
      return;
    const parent = templateRef.current.parentElement;
    if (!parent) {
      return;
    }
    if (typeof forwardedRef === "function") {
      forwardedRef(templateRef.current);
    } else if (forwardedRef && typeof forwardedRef === "object") {
      forwardedRef.current = templateRef.current;
    }
    const shadowOpts = shadowOptsRef.current;
    if (shadowOpts.mode !== shadowrootmode) {
      console.error(getMismatchErr("mode"));
    } else if (shadowOpts.delegatesFocus !== delegatesFocus) {
      console.error(getMismatchErr("delegatesFocus"));
    }
    if (shadowrootmode && !shadowRootRef.current) {
      const existingShadowRoot = parent.shadowRoot;
      if (existingShadowRoot) {
        shadowRootRef.current = existingShadowRoot;
      } else {
        try {
          shadowRootRef.current = parent.attachShadow(shadowOpts);
        } catch (error) {
          console.warn(ATTACH_SHADOW_ERR);
          console.groupCollapsed("Original Error...");
          console.error(error);
          console.groupEnd();
        }
      }
    }
    setInitialized(true);
  }, [templateRef, shadowrootmode, forwardedRef, delegatesFocus]);
  const childrenWithStyle = /* @__PURE__ */ React3.createElement(React3.Fragment, null, /* @__PURE__ */ React3.createElement("style", { dangerouslySetInnerHTML: {
    __html: cssStrings.length > 0 ? cssStrings.join("\n") : ""
  } }), children);
  if (!shadowrootmode) {
    return /* @__PURE__ */ React3.createElement("template", { ref: templateRef, ...forwardedProps }, childrenWithStyle);
  }
  if (initialized) {
    try {
      if (shadowRootRef.current === null)
        throw new Error("Shadow root cannot be null.");
      return ReactDOM.createPortal(childrenWithStyle, shadowRootRef.current);
    } catch (error) {
      console.error(RENDER_SHADOW_ERR);
      console.groupCollapsed("Original Error...");
      console.error(error);
      console.groupEnd();
      return /* @__PURE__ */ React3.createElement(React3.Fragment, null);
    }
  }
  const dsdProps = dsdSupported ? { shadowrootmode } : {};
  return /* @__PURE__ */ React3.createElement(
    "template",
    {
      ref: templateRef,
      ...dsdProps,
      ...forwardedProps
    },
    childrenWithStyle
  );
});
var emulateDSD = (children) => {
  const childrenArray = React3.Children.toArray(children);
  const template = childrenArray.find((child) => {
    return React3.isValidElement(child) && child.type === Template;
  }) ?? /* @__PURE__ */ React3.createElement(React3.Fragment, null);
  const lightDomChildren = childrenArray.filter((child) => {
    return !React3.isValidElement(child) || child.type !== Template;
  });
  return parseSlots(lightDomChildren, template);
};
var CustomElement = React3.forwardRef((props, forwardedRef) => {
  const {
    tag: Tag,
    children,
    config,
    ...forwardedProps
  } = props;
  const shadowScopeContext = React3.useContext(ShadowScopeContext);
  const dsd = config?.dsd ?? shadowScopeContext.dsd;
  const [hasHydrated, setHasHydrated] = React3.useState(false);
  React3.useEffect(() => {
    setHasHydrated(true);
  }, []);
  return /* @__PURE__ */ React3.createElement(Tag, { ...forwardedProps, ref: forwardedRef }, dsd === "emulated" ? hasHydrated ? children : /* @__PURE__ */ React3.createElement(React3.Fragment, null, /* @__PURE__ */ React3.createElement("style", null, `${Tag} { visibility: hidden; }`), /* @__PURE__ */ React3.createElement("noscript", null, /* @__PURE__ */ React3.createElement("style", null, `${Tag} { visibility: visible; }`)), emulateDSD(children)) : children);
});

// dist/index.js
import React4 from "react";
import React32 from "react";
import ReactDOM2 from "react-dom";
import React5 from "react";
import React22 from "react";
import * as React52 from "react";
var adoptedStylesSupported2 = typeof window !== "undefined" && window.ShadowRoot?.prototype.hasOwnProperty("adoptedStyleSheets") && window.CSSStyleSheet?.prototype.hasOwnProperty("replace");
var isCSSStyleSheet2 = (stylesheet) => {
  return typeof CSSStyleSheet !== "undefined" && stylesheet instanceof CSSStyleSheet;
};
var getTaggedTemplateStr2 = (strArr, ...interpolated) => {
  return strArr.reduce((resultStr, currentStr, i) => {
    return resultStr + currentStr + (interpolated[i] ?? "");
  }, "");
};
var css2 = (strArr, ...interpolated) => {
  const styles = getTaggedTemplateStr2(strArr, ...interpolated);
  if (adoptedStylesSupported2) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styles);
    return sheet;
  }
  return styles;
};
var normalizedScope2 = `
@layer normalize {
  main { display: block; }
  h1 { font-size: 2em; margin: 0.67em 0; }
  hr { box-sizing: content-box; height: 0; overflow: visible; }
  pre { font-family: monospace, monospace; font-size: 1em; }
  a { background-color: transparent; }
  abbr[title] { border-bottom: none; text-decoration: underline; text-decoration: underline dotted; }
  b, strong { font-weight: bolder; }
  code, kbd, samp { font-family: monospace, monospace; font-size: 1em; }
  small { font-size: 80%; }
  sub, sup { font-size: 75%; line-height: 0; position: relative; vertical-align: baseline; }
  sub { bottom: -0.25em; }
  sup { top: -0.5em; }
  img { border-style: none; }
  button, input, optgroup, select, textarea { font-family: inherit; font-size: 100%; line-height: 1.15; margin: 0; }
  button, input { overflow: visible; }
  button, select { text-transform: none; }
  button, [type="button"], [type="reset"], [type="submit"] { -webkit-appearance: button; }
  button::-moz-focus-inner, [type="button"]::-moz-focus-inner, [type="reset"]::-moz-focus-inner, [type="submit"]::-moz-focus-inner { border-style: none; padding: 0; }
  button:-moz-focusring, [type="button"]:-moz-focusring, [type="reset"]:-moz-focusring, [type="submit"]:-moz-focusring { outline: 1px dotted ButtonText; }
  fieldset { padding: 0.35em 0.75em 0.625em; }
  legend { box-sizing: border-box; color: inherit; display: table; max-width: 100%; padding: 0; white-space: normal; }
  progress { vertical-align: baseline; }
  textarea { overflow: auto; }
  [type="checkbox"], [type="radio"] { box-sizing: border-box; padding: 0; }
  [type="number"]::-webkit-inner-spin-button, [type="number"]::-webkit-outer-spin-button { height: auto; }
  [type="search"] { -webkit-appearance: textfield; outline-offset: -2px; }
  [type="search"]::-webkit-search-decoration { -webkit-appearance: none; }
  ::-webkit-file-upload-button { -webkit-appearance: button; font: inherit; }
  details { display: block; }
  summary { display: list-item; }
  [hidden] { display: none; }
}
`;
var normalizedStyles2 = `
@layer normalize {
  html { line-height: 1.15; -webkit-text-size-adjust: 100%; }
  body { margin: 0; }
  template { display: none; }
}
${normalizedScope2}
`;
var DEFAULT_SHADOW_SCOPE_CONTEXT2 = {
  dsd: "on"
};
var ShadowScopeContext2 = React5.createContext(DEFAULT_SHADOW_SCOPE_CONTEXT2);
var replaceSlots2 = (children, slots) => {
  return React22.Children.map(children, (child) => {
    if (React22.isValidElement(child)) {
      if (child.type === "slot") {
        const slotName = child.props.name ?? "default";
        return slotName in slots ? /* @__PURE__ */ React22.createElement(React22.Fragment, null, slots[slotName]) : child;
      } else if ("children" in child.props) {
        return React22.cloneElement(child, {
          children: replaceSlots2(child.props.children, slots)
        });
      }
    }
    return child;
  });
};
var parseSlots2 = (children, template) => {
  const slots = {};
  React22.Children.forEach(children, (child) => {
    if (React22.isValidElement(child)) {
      const slotName = child.props.slot ?? "default";
      if (!Array.isArray(slots[slotName])) {
        slots[slotName] = [];
      }
      slots[slotName].push(child);
    }
  });
  return replaceSlots2(template.props.children, slots);
};
var declarativeShadowDOMSupported2 = null;
function checkDSDSupport2() {
  if (typeof window === "undefined")
    return true;
  if (declarativeShadowDOMSupported2 !== null)
    return declarativeShadowDOMSupported2;
  if (typeof DOMParser === "undefined")
    return false;
  const fragment = new DOMParser().parseFromString(
    '<div><template shadowrootmode="open"></template></div>',
    "text/html",
    // @ts-ignore-next-line; TS doesn't know `parseFromString` supports a 3rd argument.
    { includeShadowRoots: true }
  );
  declarativeShadowDOMSupported2 = !!fragment.querySelector("div")?.shadowRoot;
  return declarativeShadowDOMSupported2;
}
var ATTACH_SHADOW_ERR2 = `Could not attach the shadow root. The element may already have a shadow root attached. This could be due to HMR in development, and may not indicate a problem. Try refreshing the page to see if this warning goes away.

If the issue persists, other code may be conflicting by attaching a shadow root before this \`<Template>\` instance does.`;
var RENDER_SHADOW_ERR2 = `Could not render the contents in the shadow root. This could be due to HMR and a failure to reattach a closed shadow root. Refresh the page to initialize a new shadow root.

If you are experiencing this issue at runtime and not during development, you may be directly re-mounting the \`<Template>\` component. This should be avoided in favor of re-mounting its parent.`;
var getMismatchErr2 = (type) => `Could not update \`${type}\` in the shadow root options because the shadow root can only be initialized once. Refresh the page or replace the parent node with an entirely new instance.`;
var Template2 = React32.forwardRef((props, forwardedRef) => {
  const {
    children,
    delegatesFocus = false,
    shadowrootmode,
    adoptedStyleSheets = [],
    ...forwardedProps
  } = props;
  const shadowScopeContext = React32.useContext(ShadowScopeContext2);
  const { cssStyleSheets, cssStrings } = React32.useMemo(
    () => {
      const cssStyleSheets2 = [];
      const cssStrings2 = [];
      for (const stylesheet of adoptedStyleSheets) {
        if (isCSSStyleSheet2(stylesheet)) {
          cssStyleSheets2.push(stylesheet);
        } else if (typeof stylesheet === "string") {
          cssStrings2.push(stylesheet);
        } else {
          console.warn(
            "An invalid stylesheet was passed to `<Template>`, skipping..."
          );
        }
      }
      return { cssStyleSheets: cssStyleSheets2, cssStrings: cssStrings2 };
    },
    [adoptedStyleSheets]
  );
  const shadowRootRef = React32.useRef(null);
  const templateRef = React32.useRef(null);
  const shadowOptsRef = React32.useRef({
    delegatesFocus,
    mode: shadowrootmode ?? "open"
  });
  const [initialized, setInitialized] = React32.useState(false);
  const dsdSupported = React32.useMemo(
    () => checkDSDSupport2() && shadowScopeContext.dsd === "on",
    [declarativeShadowDOMSupported2, shadowScopeContext.dsd]
  );
  React32.useEffect(() => {
    if (shadowRootRef.current !== null) {
      shadowRootRef.current.adoptedStyleSheets = cssStyleSheets;
    }
  }, [shadowRootRef, initialized, cssStyleSheets]);
  React32.useEffect(() => {
    if (templateRef.current === null)
      return;
    const parent = templateRef.current.parentElement;
    if (!parent) {
      return;
    }
    if (typeof forwardedRef === "function") {
      forwardedRef(templateRef.current);
    } else if (forwardedRef && typeof forwardedRef === "object") {
      forwardedRef.current = templateRef.current;
    }
    const shadowOpts = shadowOptsRef.current;
    if (shadowOpts.mode !== shadowrootmode) {
      console.error(getMismatchErr2("mode"));
    } else if (shadowOpts.delegatesFocus !== delegatesFocus) {
      console.error(getMismatchErr2("delegatesFocus"));
    }
    if (shadowrootmode && !shadowRootRef.current) {
      const existingShadowRoot = parent.shadowRoot;
      if (existingShadowRoot) {
        shadowRootRef.current = existingShadowRoot;
      } else {
        try {
          shadowRootRef.current = parent.attachShadow(shadowOpts);
        } catch (error) {
          console.warn(ATTACH_SHADOW_ERR2);
          console.groupCollapsed("Original Error...");
          console.error(error);
          console.groupEnd();
        }
      }
    }
    setInitialized(true);
  }, [templateRef, shadowrootmode, forwardedRef, delegatesFocus]);
  const childrenWithStyle = /* @__PURE__ */ React32.createElement(React32.Fragment, null, /* @__PURE__ */ React32.createElement("style", { dangerouslySetInnerHTML: {
    __html: cssStrings.length > 0 ? cssStrings.join("\n") : ""
  } }), children);
  if (!shadowrootmode) {
    return /* @__PURE__ */ React32.createElement("template", { ref: templateRef, ...forwardedProps }, childrenWithStyle);
  }
  if (initialized) {
    try {
      if (shadowRootRef.current === null)
        throw new Error("Shadow root cannot be null.");
      return ReactDOM2.createPortal(childrenWithStyle, shadowRootRef.current);
    } catch (error) {
      console.error(RENDER_SHADOW_ERR2);
      console.groupCollapsed("Original Error...");
      console.error(error);
      console.groupEnd();
      return /* @__PURE__ */ React32.createElement(React32.Fragment, null);
    }
  }
  const dsdProps = dsdSupported ? { shadowrootmode } : {};
  return /* @__PURE__ */ React32.createElement(
    "template",
    {
      ref: templateRef,
      ...dsdProps,
      ...forwardedProps
    },
    childrenWithStyle
  );
});
var emulateDSD2 = (children) => {
  const childrenArray = React32.Children.toArray(children);
  const template = childrenArray.find((child) => {
    return React32.isValidElement(child) && child.type === Template2;
  }) ?? /* @__PURE__ */ React32.createElement(React32.Fragment, null);
  const lightDomChildren = childrenArray.filter((child) => {
    return !React32.isValidElement(child) || child.type !== Template2;
  });
  return parseSlots2(lightDomChildren, template);
};
var CustomElement2 = React32.forwardRef((props, forwardedRef) => {
  const {
    tag: Tag,
    children,
    config,
    ...forwardedProps
  } = props;
  const shadowScopeContext = React32.useContext(ShadowScopeContext2);
  const dsd = config?.dsd ?? shadowScopeContext.dsd;
  const [hasHydrated, setHasHydrated] = React32.useState(false);
  React32.useEffect(() => {
    setHasHydrated(true);
  }, []);
  return /* @__PURE__ */ React32.createElement(Tag, { ...forwardedProps, ref: forwardedRef }, dsd === "emulated" ? hasHydrated ? children : /* @__PURE__ */ React32.createElement(React32.Fragment, null, /* @__PURE__ */ React32.createElement("style", null, `${Tag} { visibility: hidden; }`), /* @__PURE__ */ React32.createElement("noscript", null, /* @__PURE__ */ React32.createElement("style", null, `${Tag} { visibility: visible; }`)), emulateDSD2(children)) : children);
});
var cache = {
  base: css2`
    @layer {
      /*
      Accessibility issues with display: contents; only affect semantic tags.
      <react-shadow-scope> lacks semantics (by default.)
      The @layer makes this trivial to override if necessary.
      */
      :host {
        display: contents;
      }
    }
  `,
  normalize: css2`${normalizedScope2}`,
  stylesheets: /* @__PURE__ */ new Map()
};
var Scope = React4.forwardRef(
  (props, forwardedRef) => {
    const {
      children,
      tag = "react-shadow-scope",
      stylesheet,
      stylesheets = [],
      href,
      hrefs = [],
      pendingStyles = css2`:host { visibility: hidden; }`,
      slottedContent,
      normalize = true,
      config,
      __transform = (s) => s,
      className,
      ...forwardedProps
    } = props;
    const [hrefsLoaded, setHrefsLoaded] = React4.useState(false);
    const allHrefs = React4.useMemo(
      () => typeof href !== "undefined" ? [href, ...hrefs] : hrefs,
      [href, hrefs]
    );
    const pending = typeof window !== "undefined" && allHrefs.length > 0 && !hrefsLoaded;
    const localStyleSheets = React4.useMemo(
      () => {
        const _localStylesheets = [
          cache.normalize,
          cache.base,
          ...stylesheets
        ];
        if (pending)
          _localStylesheets.push(pendingStyles);
        if (typeof stylesheet !== "undefined")
          _localStylesheets.push(stylesheet);
        return _localStylesheets;
      },
      [pending, stylesheet, stylesheets]
    );
    const [allStyleSheets, setAllStyleSheets] = React4.useState(localStyleSheets);
    React4.useEffect(() => {
      const _allStyleSheets = [...localStyleSheets];
      const abortControllers = [];
      const requests = [];
      for (const currentHref of allHrefs) {
        if (cache.stylesheets.has(currentHref)) {
          const currentCssStyleSheet = cache.stylesheets.get(currentHref);
          if (typeof currentCssStyleSheet !== "undefined") {
            _allStyleSheets.push(currentCssStyleSheet);
            continue;
          }
        }
        const abortController = new AbortController();
        abortControllers.push(abortController);
        requests.push(
          fetch(currentHref, { signal: abortController.signal }).then(
            async (response) => ({ currentHref, text: __transform(await response.text()) })
          )
        );
      }
      if (requests.length > 0) {
        Promise.all(requests).then((cssResponses) => {
          for (const { currentHref, text } of cssResponses) {
            const currentCssStyleSheet = css2`${text}`;
            _allStyleSheets.push(currentCssStyleSheet);
            cache.stylesheets.set(currentHref, currentCssStyleSheet);
          }
          setAllStyleSheets(_allStyleSheets);
          setHrefsLoaded(true);
        });
      } else {
        setAllStyleSheets(_allStyleSheets);
        setHrefsLoaded(true);
      }
      return () => {
        for (const abortController of abortControllers) {
          abortController.abort();
        }
      };
    }, [pending, stylesheet, stylesheets]);
    const convertedProps = className ? { class: className } : {};
    return /* @__PURE__ */ React4.createElement(
      CustomElement2,
      {
        ref: forwardedRef,
        tag,
        config,
        ...convertedProps,
        ...forwardedProps
      },
      /* @__PURE__ */ React4.createElement(Template2, { shadowrootmode: "open", adoptedStyleSheets: allStyleSheets }, !hrefsLoaded ? allHrefs.map((href2) => /* @__PURE__ */ React4.createElement(React4.Fragment, { key: href2 }, /* @__PURE__ */ React4.createElement("link", { rel: "preload", href: href2, as: "style" }), /* @__PURE__ */ React4.createElement("link", { rel: "stylesheet", href: href2 }))) : /* @__PURE__ */ React4.createElement(React4.Fragment, null), children),
      slottedContent
    );
  }
);
var Tailwind = React52.forwardRef(
  (props, forwardedRef) => {
    const {
      children,
      tag = "react-shadow-scope",
      href = "/tailwind.css",
      customStyles,
      slottedContent,
      pendingStyles = css2`:host { visibility: hidden; }`,
      config,
      ...forwardedProps
    } = props;
    const transformForTailwind = (cssString) => {
      return cssString.replace(
        /(?:^|\s)(html)(?:[^-_a-z])/gi,
        ":host"
      );
    };
    return /* @__PURE__ */ React52.createElement(
      Scope,
      {
        ...forwardedProps,
        ref: forwardedRef,
        tag,
        stylesheet: customStyles,
        stylesheets: [],
        href,
        hrefs: [],
        pendingStyles,
        normalize: false,
        config,
        slottedContent,
        __transform: transformForTailwind
      },
      children
    );
  }
);

// src/scope.tsx
var cache2 = {
  base: css`
    @layer {
      /*
      Accessibility issues with display: contents; only affect semantic tags.
      <react-shadow-scope> lacks semantics (by default.)
      The @layer makes this trivial to override if necessary.
      */
      :host {
        display: contents;
      }
    }
  `,
  normalize: css`${normalizedScope}`,
  stylesheets: /* @__PURE__ */ new Map()
};
var Scope2 = React6.forwardRef(
  (props, forwardedRef) => {
    const {
      children,
      tag = "react-shadow-scope",
      stylesheet,
      stylesheets = [],
      href,
      hrefs = [],
      pendingStyles = css`:host { visibility: hidden; }`,
      slottedContent,
      normalize = true,
      config,
      __transform = (s) => s,
      className,
      ...forwardedProps
    } = props;
    const [hrefsLoaded, setHrefsLoaded] = React6.useState(false);
    const allHrefs = React6.useMemo(
      () => typeof href !== "undefined" ? [href, ...hrefs] : hrefs,
      [href, hrefs]
    );
    const pending = typeof window !== "undefined" && allHrefs.length > 0 && !hrefsLoaded;
    const localStyleText = [...stylesheets, stylesheet].map((s) => isCSSStyleSheet2(s) ? Array.from(s.cssRules).map((r) => r.cssText).join("") : s).join("");
    const localStyleSheets = React6.useMemo(
      () => {
        const _localStylesheets = [
          cache2.normalize,
          cache2.base,
          ...stylesheets
        ];
        if (pending)
          _localStylesheets.push(pendingStyles);
        if (typeof stylesheet !== "undefined")
          _localStylesheets.push(stylesheet);
        return _localStylesheets;
      },
      [pending, localStyleText]
    );
    const [allStyleSheets, setAllStyleSheets] = React6.useState(localStyleSheets);
    React6.useEffect(() => {
      const _allStyleSheets = [...localStyleSheets];
      const abortControllers = [];
      const requests = [];
      for (const currentHref of allHrefs) {
        if (cache2.stylesheets.has(currentHref)) {
          const currentCssStyleSheet = cache2.stylesheets.get(currentHref);
          if (typeof currentCssStyleSheet !== "undefined") {
            _allStyleSheets.push(currentCssStyleSheet);
            continue;
          }
        }
        const abortController = new AbortController();
        abortControllers.push(abortController);
        requests.push(
          fetch(currentHref, { signal: abortController.signal }).then(
            async (response) => ({ currentHref, text: __transform(await response.text()) })
          )
        );
      }
      if (requests.length > 0) {
        Promise.all(requests).then((cssResponses) => {
          for (const { currentHref, text } of cssResponses) {
            const currentCssStyleSheet = css`${text}`;
            _allStyleSheets.push(currentCssStyleSheet);
            cache2.stylesheets.set(currentHref, currentCssStyleSheet);
          }
          setAllStyleSheets(_allStyleSheets);
          setHrefsLoaded(true);
        });
      } else {
        setAllStyleSheets(_allStyleSheets);
        setHrefsLoaded(true);
      }
      return () => {
        for (const abortController of abortControllers) {
          abortController.abort();
        }
      };
    }, [pending, localStyleText]);
    const convertedProps = className ? { class: className } : {};
    return /* @__PURE__ */ React6.createElement(
      CustomElement,
      {
        ref: forwardedRef,
        tag,
        config,
        ...convertedProps,
        ...forwardedProps
      },
      /* @__PURE__ */ React6.createElement(Template, { shadowrootmode: "open", adoptedStyleSheets: allStyleSheets }, !hrefsLoaded ? allHrefs.map((href2) => /* @__PURE__ */ React6.createElement(React6.Fragment, { key: href2 }, /* @__PURE__ */ React6.createElement("link", { rel: "preload", href: href2, as: "style" }), /* @__PURE__ */ React6.createElement("link", { rel: "stylesheet", href: href2 }))) : /* @__PURE__ */ React6.createElement(React6.Fragment, null), children),
      slottedContent
    );
  }
);

// src/tailwind.tsx
import * as React7 from "react";
var Tailwind2 = React7.forwardRef(
  (props, forwardedRef) => {
    const {
      children,
      tag = "react-shadow-scope",
      href = "/tailwind.css",
      customStyles,
      slottedContent,
      pendingStyles = css`:host { visibility: hidden; }`,
      config,
      ...forwardedProps
    } = props;
    const transformForTailwind = (cssString) => {
      return cssString.replace(
        /(?:^|\s)(html)(?:[^-_a-z])/gi,
        ":host"
      );
    };
    return /* @__PURE__ */ React7.createElement(
      Scope2,
      {
        ...forwardedProps,
        ref: forwardedRef,
        tag,
        stylesheet: customStyles,
        stylesheets: [],
        href,
        hrefs: [],
        pendingStyles,
        normalize: false,
        config,
        slottedContent,
        __transform: transformForTailwind
      },
      children
    );
  }
);
export {
  CustomElement,
  Scope2 as Scope,
  ShadowScopeConfigProvider,
  Tailwind2 as Tailwind,
  Template,
  adoptedStylesSupported,
  css,
  isCSSStyleSheet,
  normalizedScope,
  normalizedStyles,
  useCSS
};
